'use client'

import { KeyboardEvent, useState } from 'react'
import Image from 'next/image'
import { Container } from '@/components/Container'
import { SectionDescription } from '@/components/SectionDescription'
import { SectionTitle } from '@/components/SectionTitle'
import { Tag } from '@/components/Tag'
import { founders } from '@/data/founders'
import { FounderPhoto } from './FounderPhoto'

export function FoundersSection() {
  const [activeIndex, setActiveIndex] = useState(0)
  const activeFounder = founders[activeIndex]
  const isFirstSlide = activeIndex === 0
  const isLastSlide = activeIndex === founders.length - 1

  function selectSlide(index: number) {
    setActiveIndex(index)
  }

  function focusSlideBullet(index: number) {
    document.getElementById(`founders-slide-tab-${index}`)?.focus()
  }

  function handleBulletKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    let nextIndex: number | null = null

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(index - 1, 0)
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(index + 1, founders.length - 1)
    }

    if (event.key === 'Home') {
      nextIndex = 0
    }

    if (event.key === 'End') {
      nextIndex = founders.length - 1
    }

    if (nextIndex === null) {
      return
    }

    event.preventDefault()
    selectSlide(nextIndex)
    requestAnimationFrame(() => focusSlideBullet(nextIndex))
  }

  function goToPreviousSlide() {
    if (isFirstSlide) {
      return
    }

    selectSlide(activeIndex - 1)
  }

  function goToNextSlide() {
    if (isLastSlide) {
      return
    }

    selectSlide(activeIndex + 1)
  }

  return (
    <section className="bg-white-950 py-12 text-white-50 md:py-20">
      <Container>
        <div className="max-w-210">
          <Tag variant="yellow">Os fundadores</Tag>

          <SectionTitle className="mt-5 md:mt-6">
            Conheça nossos fundadores
          </SectionTitle>
          <SectionDescription className="mt-4 text-white-600 md:mt-6">
            Saiba quem vai construir a agenda de acessibilidade com a sua
            empresa.
          </SectionDescription>
        </div>

        <section
          id='fundadores'
          className="mt-6 md:mt-10"
          aria-label="Fundadores"
        >
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {`${activeFounder.name}. ${activeFounder.description}`}
          </p>

          <article
            className="grid gap-6 md:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.1fr)] md:gap-10"
          >
            <FounderPhoto founder={activeFounder} />

            <div className="flex flex-col md:max-h-155">
              <h3 className="text-2xl leading-8 font-heading md:text-[40px] font-bold md:leading-12 text-white-50">
                {activeFounder.name}
              </h3>

              <div
                className="mt-6 grid gap-5 overflow-visible font-sans text-base font-normal leading-5 text-white-50 md:overflow-y-auto md:mt-10 md:pr-3 md:[scrollbar-color:#f1bd03_#373737] md:scrollbar-thin md:[&::-webkit-scrollbar]:w-2 md:[&::-webkit-scrollbar-thumb]:rounded-full md:[&::-webkit-scrollbar-thumb]:bg-lightning-yellow-500 md:[&::-webkit-scrollbar-track]:rounded-full md:[&::-webkit-scrollbar-track]:bg-white-900"
              >
                {activeFounder.description.split('\n\n').map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
          </article>

          <div className="mt-6 flex items-center justify-between gap-4 md:mt-10 md:grid md:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.1fr)] md:gap-12">
            <div
              className="flex items-center gap-2"
              aria-label="Fundadores"
            >
              {founders.map((founder, index) => {
                const isActive = index === activeIndex

                return (
                  <button
                    key={founder.name}
                    id={`founders-slide-tab-${index}`}
                    type="button"
                    className="flex size-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white-950"
                    aria-label={founder.name}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => selectSlide(index)}
                    onKeyDown={(event) => handleBulletKeyDown(event, index)}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-2 rounded-full transition-[width,background-color] ${
                        isActive ? 'w-6 bg-white-50' : 'w-2 bg-white-700'
                      }`}
                    />
                  </button>
                )
              })}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-lightning-yellow-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white-950 aria-disabled:cursor-not-allowed aria-disabled:bg-black-100"
                aria-label="Anterior"
                aria-disabled={isFirstSlide}
                aria-describedby='fundadores'
                onClick={goToPreviousSlide}
              >
                <Image
                  src="/assets/icons/arrow-left.svg"
                  alt=""
                  width={12}
                  height={10}
                  aria-hidden="true"
                />
              </button>

              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-lightning-yellow-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white-950 aria-disabled:cursor-not-allowed aria-disabled:bg-black-100"
                aria-label="Próximo"
                aria-disabled={isLastSlide}
                aria-describedby='fundadores'
                onClick={goToNextSlide}
              >
                <Image
                  src="/assets/icons/arrow-right.svg"
                  alt=""
                  width={12}
                  height={10}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </section>
      </Container>
    </section>
  )
}
